import { QueryKey } from '@tanstack/react-query'
import { HorizonOpt } from '@weenat/client/dist/core/horizons'
import { useMetricProvider } from '@weenat/client/dist/core/metrics'
import { useToggleIsFavorite } from '@weenat/client/dist/core/plots'
import {
  computeHasNotEmitted,
  getMetricLastUpdateFromSummary
} from '@weenat/client/dist/core/summary'
import { UniverseMetric } from '@weenat/client/dist/core/universes'
import { isFertigationMetric, isFrost, isHydricStress } from '@weenat/client/dist/enums/MetricIds'
import { PlotSummary, isPlotSummary } from '@weenat/client/dist/resources/measurements.type'
import { isArvalisPlotSummary } from '@weenat/client/dist/resources/waterBalanceArvalis'
import { useIntl } from '@weenat/wintl'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import useToasts from 'app/src/hooks/useToasts'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import { IconProps } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import InteractiveCard from 'app/src/kit/InteractiveCard'
import Text from 'app/src/kit/Text'
import UpdateInformation, { UpdateInformationProps } from 'app/src/kit/UpdateInformation'
import NoEmissionEmptyState from 'app/src/kit/empty-state/NoEmissionEmptyState'
import { FlexProps } from 'app/src/kit/primitives/themeMappings/props'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import { MouseEvent, ReactNode, memo, useCallback } from 'react'
import { styled } from 'styled-components'
import NoDataForHorizon from './NoDataForHorizon'
import PlotListItemMetricSummary from './plotListItem/PlotListItemMetricSummary'

const Titles = styled(Flex)`
  flex: 1;
  align-items: center;

  gap: 4px;
  overflow: hidden;
`

interface PlotCardHeaderProps extends FlexProps {
  isFavorite?: boolean
  isFavoriteRequestLoading?: boolean
  name: string
  reverseGeocoding: string | null
  onFavoritePress?: IconProps['onPress']
  Icon: ReactNode
  onClose?: () => void
}

const DumbPlotCardHeader: FC<PlotCardHeaderProps> = ({
  name,
  reverseGeocoding,
  isFavoriteRequestLoading,
  isFavorite,
  onFavoritePress,
  Icon,
  onClose,
  ref: _notUsed,
  ...flexProps
}) => {
  const { t } = useIntl()
  const [{ marker_redesign, on_nav_favorites }] = useToggleFeature()

  return (
    <DelimitedFlex $alignItems='center' $p='md' $gap='md' {...flexProps}>
      {marker_redesign ? null : Icon}
      <Titles>
        <Flex $flexDirection='column' $minWidth={0} $flex={1}>
          <Text $flexed $fontWeight='bold' $ellipsis>
            {name}
          </Text>
          <Text $fontSize='sm' $color={'grayscale.700'} $ellipsis>
            {!isNil(reverseGeocoding)
              ? reverseGeocoding
              : t('models.device.unknown_location', { capitalize: true })}
          </Text>
        </Flex>
      </Titles>
      {onFavoritePress && !on_nav_favorites ? (
        <TextTooltip
          restMs={1000}
          content={t(`actions.${!isFavorite ? 'add_to_favorite' : 'remove_from_favorite'}`)}
        >
          <Icons.Star
            $p='md'
            $size='lg'
            $isLoading={isFavoriteRequestLoading}
            onPress={onFavoritePress}
            $color={isFavorite ? 'primary.500' : 'grayscale.300'}
          />
        </TextTooltip>
      ) : null}
      {onClose ? (
        <TextTooltip restMs={1000} content={t(`actions.close`)}>
          <Icons.Close $backgroundColor={'grayscale.100'} $p='md' $size='lg' onPress={onClose} />
        </TextTooltip>
      ) : null}
    </DelimitedFlex>
  )
}

export const PlotCardHeader = memo(DumbPlotCardHeader)

const FooterContainer = styled(DelimitedFlex)`
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  padding: ${(p) => p.theme.spacings.md}px;
`

type PlotCardFooter = UpdateInformationProps

const DumbPlotCardFooter: FC<PlotCardFooter> = ({
  warningThreshold,
  dangerThreshold,
  isVirtualMetric,
  timestamp,
  name,
  timezone
}) => {
  return (
    <FooterContainer $isDelimitedOnTop>
      <UpdateInformation
        timezone={timezone}
        timestamp={timestamp}
        isVirtualMetric={isVirtualMetric}
        warningThreshold={warningThreshold}
        dangerThreshold={dangerThreshold}
        name={name}
      />
    </FooterContainer>
  )
}

export const PlotCardFooter = memo(DumbPlotCardFooter)

export const StyledCard = styled(InteractiveCard)<{ $isSelected: boolean }>`
  height: 100%;
  width: 352px;

  border-width: 1px;
  border-radius: ${(p) => p.theme.radiuses.lg}px;

  ${(p) =>
    p.$isSelected
      ? `
          border-color: ${p.theme.colors.primary[500]};
        `
      : ''}

  padding: 0;
  overflow: hidden;
`

const PlotIcon = <Icons.PlotsFilled $size='lg' />

interface PlotCardProps {
  availableHorizonOptions: HorizonOpt[]
  focusedHorizon: number | null
  focusedMetric: UniverseMetric
  hasNoDataForHorizon: boolean
  isSelected: boolean
  onHorizonChange: (newHorizon: number | null) => void
  summariesQueryCacheKey: QueryKey
  summary: PlotSummary
  isExpertOrg: boolean
  onPress?: () => void
  isTooltip?: boolean
  onClose?: () => void
}

/**
 * @deprecated Use SummaryCard instead. Remove this component when the `marker_redesign` feature flag is removed.
 */
const PlotCard: FC<PlotCardProps> = ({
  availableHorizonOptions = [],
  focusedHorizon = null,
  focusedMetric,
  hasNoDataForHorizon = false,
  isSelected = false,
  onHorizonChange,
  summariesQueryCacheKey,
  summary,
  isExpertOrg,
  onPress,
  onClose
}) => {
  const { t } = useIntl()
  const { addSuccessToast } = useToasts()
  const [{ marker_redesign, smv }] = useToggleFeature()

  const { name, isFavorite, timezone } = summary.metadata.plot

  const { mutation: toggleIsFavorite, request: isFavoriteRequest } = useToggleIsFavorite({
    summary,
    summariesQueryCacheKey,
    onSuccess: (data) => {
      addSuccessToast(
        t(
          data.isFavorite
            ? 'actions.add_plot_to_favorites_success'
            : 'actions.remove_plot_from_favorites_success',
          { plotName: name }
        )
      )
    }
  })

  const onFavoritePress = useCallback(
    async (e: MouseEvent<Element>) => {
      // Otherwise we open the summary
      e.preventDefault()
      await toggleIsFavorite()
    },
    [toggleIsFavorite]
  )

  const {
    isVirtualDevice,
    warningThreshold,
    lastUpdateThresholds,
    name: providerName,
    deviceIds
  } = useMetricProvider({
    resource: summary.metadata.plot,
    metric: focusedMetric,
    isExpertOrg,
    smv
  })

  const lastUpdate = getMetricLastUpdateFromSummary({
    summary,
    metric: focusedMetric,
    horizonId: deviceIds.length > 1 ? null : focusedHorizon
  })

  const { isRestricted, reason } = useIsAppAccessRestricted()

  const isArvalisPlot = isArvalisPlotSummary(summary)
  const plotIsSetupForCurrentSeason = isArvalisPlot && summary.plotIsSetupForCurrentSeason
  const shouldConfigureArvalis = isArvalisPlot && !plotIsSetupForCurrentSeason
  const arvalisWBComputing = isArvalisPlot && summary.waterBalanceStatus === 'running'
  const arvalisWBError = isArvalisPlot && summary.waterBalanceStatus === 'error'
  const shouldConfigureMetric =
    isPlotSummary(summary) && !isNil(focusedMetric)
      ? (summary.metadata.notConfigured?.includes(focusedMetric) ?? false)
      : false

  const hasNotEmitted = computeHasNotEmitted(summary, focusedMetric, isVirtualDevice)

  let footer: ReactNode = (
    <PlotCardFooter
      timestamp={lastUpdate}
      timezone={timezone}
      isVirtualMetric={marker_redesign ? undefined : isVirtualDevice}
      warningThreshold={marker_redesign ? lastUpdateThresholds.warningThreshold : warningThreshold}
      dangerThreshold={lastUpdateThresholds.dangerThreshold}
      name={providerName}
    />
  )
  if (hasNoDataForHorizon || isFrost(focusedMetric)) {
    footer = null
  } else if (!hasNotEmitted && (shouldConfigureArvalis || shouldConfigureMetric)) {
    footer = (
      <DelimitedFlex $isDelimitedOnTop $py={shouldConfigureMetric ? 'md' : 'lg'}>
        <Text $fontWeight='bold' style={{ margin: 'auto' }}>
          {t('actions.configure')}
        </Text>
      </DelimitedFlex>
    )
  }

  return (
    <StyledCard $isSelected={isSelected} onPress={onPress}>
      <PlotCardHeader
        isFavorite={isFavorite}
        isFavoriteRequestLoading={isFavoriteRequest.isPending}
        name={name}
        reverseGeocoding={summary.metadata.plot.reverseGeocoding}
        onFavoritePress={onFavoritePress}
        Icon={PlotIcon}
        onClose={onClose}
      />

      {!isNil(focusedMetric) ? (
        shouldConfigureArvalis || shouldConfigureMetric || arvalisWBComputing || arvalisWBError ? (
          <Flex
            $flexDirection='column'
            $alignItems='center'
            $justifyContent='center'
            $flex={1}
            $px='md'
            $py={shouldConfigureArvalis ? 'xs' : 'md'}
          >
            <Box $p='sm'>
              <Text $textAlign='center'>
                {t(
                  shouldConfigureArvalis
                    ? 'dss.waterBalance.arvalis_plot_creation.not_configure_summary'
                    : arvalisWBComputing
                      ? 'dss.waterBalance.arvalis_plot_creation.computing'
                      : arvalisWBError
                        ? 'dss.waterBalance.arvalis_plot_creation.error_fetching'
                        : isFertigationMetric(focusedMetric)
                          ? 'models.plot.misc.setup_irrigation'
                          : 'errors.unknown'
                )}
              </Text>
            </Box>
          </Flex>
        ) : hasNotEmitted ? (
          <NoEmissionEmptyState
            metrics={[focusedMetric]}
            title={t('weedget.not_emitted_yet.wait_message')}
            description={t('weedget.not_emitted_yet.waiting_on_plot')}
          />
        ) : isHydricStress(focusedMetric) && hasNoDataForHorizon ? (
          <NoDataForHorizon
            metric={focusedMetric}
            currentHorizon={focusedHorizon}
            onHorizonChange={onHorizonChange}
            availableHorizonOptions={availableHorizonOptions}
          />
        ) : (
          <Flex
            $flexDirection='column'
            $alignItems='center'
            $justifyContent='center'
            $flex={1}
            $p='md'
          >
            <PlotListItemMetricSummary
              metric={focusedMetric}
              data={summary.data}
              focusedHorizon={focusedHorizon}
              isRestricted={isRestricted}
              reason={reason}
              isVirtualDevice={isVirtualDevice}
              provider={providerName}
              timezone={timezone}
            />
          </Flex>
        )
      ) : null}

      {footer}
    </StyledCard>
  )
}

export default memo(PlotCard)
